const DAY_IN_MS = 24 * 60 * 60 * 1000;
export const EXPIRING_SOON_DAYS = 30;
export const DEFAULT_VENDOR_LIMIT = 10;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDemoDate(value) {
  if (value instanceof Date) {
    return new Date(value);
  }

  if (typeof value === 'string') {
    const match = value.match(DATE_ONLY_PATTERN);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }

  return new Date(value);
}

export function toDateOnly(value) {
  if (!value) return null;

  const date = parseDemoDate(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function toIsoString(value) {
  const date = parseDemoDate(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function toDateInputString(value) {
  const date = toDateOnly(value);
  if (!date) return null;

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDisplayDate(value) {
  const date = toDateOnly(value);
  if (!date) return 'Not specified';
  return date.toLocaleDateString();
}

export function addDays(value, days) {
  const date = toDateOnly(value);
  if (!date) return null;

  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function diffInDays(fromValue, toValue) {
  const fromDate = toDateOnly(fromValue);
  const toDate = toDateOnly(toValue);
  if (!fromDate || !toDate) return null;

  return Math.round((toDate.getTime() - fromDate.getTime()) / DAY_IN_MS);
}

export function calculatePolicyStatus(policy, today = new Date()) {
  const currentDay = toDateOnly(today);
  const effectiveDate = toDateOnly(policy.effective_date);
  const expirationDate = toDateOnly(policy.expiration_date);

  if (!expirationDate || !currentDay) {
    return 'active';
  }

  if (effectiveDate && currentDay < effectiveDate) {
    return 'pending';
  }

  if (currentDay > expirationDate) {
    return 'expired';
  }

  return 'active';
}

export function getDaysUntilExpiration(policy, today = new Date()) {
  return diffInDays(today, policy.expiration_date);
}

export function getIsExpiringSoon(policy, today = new Date()) {
  const status = calculatePolicyStatus(policy, today);
  if (status !== 'active') {
    return false;
  }

  const daysUntilExpiration = getDaysUntilExpiration(policy, today);
  if (daysUntilExpiration === null) {
    return false;
  }

  return daysUntilExpiration >= 0 && daysUntilExpiration <= EXPIRING_SOON_DAYS;
}

export function enrichPolicy(policy, vendors, insuranceTypes, today = new Date()) {
  const vendor = vendors.find((item) => item.id === policy.vendor_id);
  const insuranceType = insuranceTypes.find((item) => item.id === policy.insurance_type_id) || null;
  const status = calculatePolicyStatus(policy, today);
  const daysUntilExpiration = getDaysUntilExpiration(policy, today);

  return {
    ...policy,
    vendor_name: vendor?.name || policy.vendor_name || 'Vendor',
    vendor_status: vendor?.status || 'active',
    insurance_type: insuranceType ? { id: insuranceType.id, name: insuranceType.name } : null,
    custom_field_definitions: policy.custom_field_definitions || insuranceType?.custom_fields || [],
    status,
    days_until_expiration: daysUntilExpiration,
    is_expiring_soon: status === 'active' && getIsExpiringSoon(policy, today),
  };
}

export function enrichPolicies(policies, vendors, insuranceTypes, today = new Date()) {
  return policies.map((policy) => enrichPolicy(policy, vendors, insuranceTypes, today));
}

export function buildDemoStats(policies, vendors, notifications, vendorLimit = DEFAULT_VENDOR_LIMIT) {
  const activePolicies = policies.filter((policy) => policy.status === 'active').length;
  const expiringSoon = policies.filter((policy) => policy.is_expiring_soon).length;
  const expired = policies.filter((policy) => policy.status === 'expired').length;
  const activeVendors = vendors.filter((vendor) => vendor.status === 'active' && !vendor.is_self).length;
  const unreadNotifications = notifications.filter((notification) => !notification.read).length;

  return {
    active_policies: activePolicies,
    expiring_soon: expiringSoon,
    expired,
    active_vendors: activeVendors,
    vendor_limit: vendorLimit,
    unread_notifications: unreadNotifications,
  };
}
